import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Plus, Save } from '../../icons';
import {
  Button, Card, Checkbox, DatePicker, FormField, Input, PageHeader, Select,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listLeaveRules, createLeaveRule,
  type LeaveRuleRowDto, type LeaveTierDto,
} from '../../api/hr-admin';
import {
  TIER_KIND_LABEL, tierKindOf, tierText, warningsFor, isBackdated, type TierKind,
} from '../../lib/leave-policy';
import './AttendanceAdmin.css';
import './LeavePolicy.css';

/**
 * 연차정책 — **회사가 연차 부여 규칙을 정하는 자리** (2026-08-30 · 판 J).
 *
 * ★★ **왜 필요했나.** 표(`hr_leave_rules`)도 값도 **회사별로 이미 있었고 서버 API 도 있었는데
 *    고칠 화면만 없었다.** 회사가 하나일 땐 한 번 넣어 두면 그만이지만, **회사가 늘면 계약마다
 *    손으로 DB 를 만지는 일**이 된다. 이 제품은 여러 회사가 쓴다.
 *
 * ★★ **고치는 것이 아니라 «새 판» 을 만든다.** 서버가 그렇게 정해 뒀다 — 기존 행은 안 고치고
 *    시행일자별로 쌓인다. 그래야 **지난 계산이 흔들리지 않는다**(작년 연차가 올해 규칙으로 다시
 *    계산되면 아무도 설명할 수 없다). 화면이 그 사실을 먼저 말한다.
 *
 * ★ **지금 판을 복사해서 시작한다** — 빈 종이에서 짜게 하면 한 칸을 빠뜨리고, 그 한 칸이
 *   전 직원의 연차를 바꾼다.
 * ★ **법정 최소는 바닥이다.** 막지는 않는다(회사 사정을 다 알 수 없다) — 대신 **말해 준다**.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 연차 넷) ─────────────────────────
 * `PageHeader` · `Card` · `FormField` · `Input` · `Select` · `Checkbox` · `DatePicker` · `Button`.
 *
 * ★★★ **「기존 판은 고치지 않습니다」는 화면에 남겼다** — P8 의 예외다. 정본 P3-1 이
 *    「등록만 있고 수정이 없는 마스터 화면은 미완성」이라고 못 박아 두었으므로, **수정이 없는
 *    이유가 보이지 않으면 이 화면은 «덜 만든 것»으로 읽힌다.** 「왜 그래야 하는가」(지난 계산이
 *    흔들린다 · 소급 시행일자)는 도움말로 갔다.
 * ★★ **폼 칸은 전부 `FormField` 로.** 다만 구간 칸의 이름은 여전히 `aria-label` 이 만든다 —
 *    한 폼에 「일수」가 여럿이라 보이는 라벨만으로는 **몇 번째 구간인지 갈리지 않는다**.
 */
const todayIso = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

interface Draft {
  effectiveFrom: string;
  basis: string;
  /**
   * 회계연도 시작월 (2026-08-30 · 판 J) — `basis = FISCAL_YEAR` 일 때만 칸이 뜬다.
   * ★ 「회계연도 기준」은 고를 수 있었는데 **언제 시작하는지는 못 정했다.** 3월·7월 시작 회사에서
   *   사용기간이 통째로 어긋나고, 그 위에 얹히는 **연차촉진 기한이 몇 달씩 틀린다.**
   */
  fiscalStartMonth: number;
  maxDays: number;
  firstYearBonus: number;
  carryOver: boolean;
  tiers: LeaveTierDto[];
}

const emptyTier = (kind: TierKind): LeaveTierDto => {
  if (kind === 'MONTHLY') return { months: 0, perMonth: 1, max: 11 };
  if (kind === 'STEP') return { months: 36, days: 16, everyMonths: 24, plus: 1 };
  return { months: 12, days: 15 };
};

const BASIS_OPTIONS = [
  { value: 'HIRE_DATE', label: '입사일 기준' },
  { value: 'FISCAL_YEAR', label: '회계연도 기준' },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `${i + 1}월` }));

const KIND_OPTIONS = (Object.keys(TIER_KIND_LABEL) as TierKind[])
  .map((k) => ({ value: k, label: TIER_KIND_LABEL[k] }));

export function LeavePolicyPage(): JSX.Element {
  const [rules, setRules] = useState<LeaveRuleRowDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await listLeaveRules();
      setRules(res.rules);
    } catch {
      setError('연차정책을 불러오지 못했습니다.');
      setRules(null);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const current = rules?.find((r) => r.isCurrent) ?? rules?.[0] ?? null;

  /** ★ 지금 판을 복사해서 시작한다 — 빈 종이에서 짜면 한 칸을 빠뜨린다. */
  const startDraft = () => {
    setSaved(null);
    setDraft({
      effectiveFrom: todayIso(),
      basis: current?.basis ?? 'HIRE_DATE',
      fiscalStartMonth: current?.fiscalStartMonth ?? 1,
      maxDays: current?.maxDays ?? 25,
      firstYearBonus: current?.firstYearBonus ?? 0,
      carryOver: current?.carryOver ?? false,
      tiers: current ? current.tiers.map((t) => ({ ...t })) : [emptyTier('MONTHLY'), emptyTier('FIXED')],
    });
  };

  const warnings = useMemo(
    () => (draft ? warningsFor({ tiers: draft.tiers, maxDays: draft.maxDays }) : []),
    [draft],
  );
  const backdated = draft ? isBackdated(draft.effectiveFrom, todayIso()) : false;

  const patchTier = (i: number, patch: Partial<LeaveTierDto>) => {
    setDraft((d) => (d ? { ...d, tiers: d.tiers.map((t, k) => (k === i ? { ...t, ...patch } : t)) } : d));
  };
  const setKind = (i: number, kind: TierKind) => {
    setDraft((d) => (d ? {
      ...d,
      // 종류를 바꾸면 그 종류의 칸만 남긴다 — 섞이면 서버가 다른 뜻으로 읽는다.
      tiers: d.tiers.map((t, k) => (k === i ? { ...emptyTier(kind), months: t.months } : t)),
    } : d));
  };

  const submit = async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    try {
      await createLeaveRule({
        effectiveFrom: draft.effectiveFrom,
        tiers: draft.tiers,
        maxDays: draft.maxDays,
        basis: draft.basis,
        fiscalStartMonth: draft.fiscalStartMonth,
        firstYearBonus: draft.firstYearBonus,
        carryOver: draft.carryOver,
      });
      setSaved(`${draft.effectiveFrom}부터 적용되는 판을 만들었습니다.`);
      setDraft(null);
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      setError(code === 'RULE_EXISTS'
        ? '같은 시행일자의 판이 이미 있습니다 — 날짜를 바꾸세요(기존 판은 고치지 않습니다).'
        : '만들지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hra lvp">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/leave-policy')}
        scope={scopeFor('/erp/attendance/leave-policy')}
        title="연차정책"
        /*
          ★★★ 「고치지 않는다」만은 화면에 남는다 — 정본 P3-1 이 「수정 없는 마스터 화면은
          미완성」이라 못 박아 두었으므로, **수정이 없는 이유가 안 보이면 덜 만든 화면**으로 읽힌다.
        */
        description={(
          <>
            연차를 <b>얼마나 주는지</b>를 회사가 정합니다. <b>기존 판은 고치지 않습니다</b> —
            시행일자를 달리해 <b>새 판을 만들면</b> 그날부터 그 규칙이 쓰입니다.
          </>
        )}
        help={{
          title: '왜 고치지 않고 쌓는가',
          body: (
            <>
              <p>서버는 판을 <b>시행일자별로 쌓고</b>, 각 연도는 그때 유효했던 판으로 계산합니다.
                그래야 <b>지난 계산이 흔들리지 않습니다</b> — 기존 행을 고치면 작년 연차가 올해
                규칙으로 다시 계산되어 아무도 설명할 수 없습니다.</p>
              <p><b>새 판은 지금 판을 그대로 복사해</b> 시작합니다. 빈 종이에서 짜면 한 칸을
                빠뜨리고, 그 한 칸이 전 직원의 연차를 바꿉니다.</p>
              <p><b>법정 최소는 바닥입니다.</b> 적게 주는 판도 막지는 않습니다 — 회사 사정을 다 알
                수 없기 때문입니다. 대신 <b>말해 줍니다</b>.</p>
              <p>시행일자를 <b>오늘보다 앞</b>으로 두면 그 사이에 이미 계산된 연차가 달라질 수
                있습니다. 같은 시행일자의 판은 두 벌 만들 수 없습니다.</p>
            </>
          ),
        }}
      />

      {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
      {saved && <p className="lvp__ok" role="status"><Check size={14} aria-hidden />{saved}</p>}

      {rules === null ? (
        <p className="lvp__state">불러오는 중…</p>
      ) : (
        <>
          <section className="lvp__list" aria-label="판 목록">
            {rules.length === 0 && (
              <p className="lvp__state">
                아직 만든 판이 없습니다 — 지금은 <b>법정 최소</b>로 계산합니다.
              </p>
            )}
            {rules.map((r) => (
              <Card key={r.id} headless className={`lvp__card${r.isCurrent ? ' is-current' : ''}`}>
                <header className="lvp__cardhead">
                  <b>{r.effectiveFrom}부터</b>
                  {r.isCurrent && <span className="lvp__now">지금 적용</span>}
                  <span className="lvp__basis">
                    {r.basis === 'FISCAL_YEAR'
                      ? `회계연도 기준 · ${r.fiscalStartMonth ?? 1}월 시작`
                      : '입사일 기준'}
                    {' · '}상한 {r.maxDays}일
                    {r.firstYearBonus > 0 ? ` · 1년차 +${r.firstYearBonus}일` : ''}
                    {r.carryOver ? ' · 이월함' : ''}
                  </span>
                </header>
                <ul className="lvp__tiers">
                  {r.tiers.map((t, i) => <li key={i}>{tierText(t, r.maxDays)}</li>)}
                </ul>
              </Card>
            ))}
          </section>

          {!draft ? (
            <Button className="lvp__start" iconLeft={<Plus size={16} aria-hidden />} onClick={startDraft}>
              새 판 만들기
            </Button>
          ) : (
            <section className="lvp__form" aria-label="새 판 만들기">
              <h2 className="lvp__formt">새 판 만들기</h2>
              {/* 「지금 판을 복사해 왔다」는 **이 초안의 상태**다 — 값에 따라 달라지므로 화면에 남는다. */}
              <p className="lvp__hint">
                지금 판을 <b>그대로 복사</b>해 왔습니다 — 바꿀 것만 고치세요.
              </p>

              <div className="lvp__grid">
                <FormField label="시행일자">
                  <DatePicker
                    value={draft.effectiveFrom}
                    onChange={(v) => setDraft({ ...draft, effectiveFrom: v ?? todayIso() })}
                  />
                </FormField>
                <FormField label="기준">
                  <Select
                    value={draft.basis}
                    onChange={(v) => setDraft({ ...draft, basis: v })}
                    options={BASIS_OPTIONS}
                  />
                </FormField>
                {/* ★ 회계연도를 골랐을 때만 뜬다 — 입사일 기준에서는 뜻이 없는 칸이라 두면 헷갈린다. */}
                {draft.basis === 'FISCAL_YEAR' && (
                  <FormField label="시작월">
                    <Select
                      ariaLabel="회계연도 시작월"
                      value={String(draft.fiscalStartMonth)}
                      onChange={(v) => setDraft({ ...draft, fiscalStartMonth: Number(v) })}
                      options={MONTH_OPTIONS}
                    />
                  </FormField>
                )}
                <FormField label="상한(일)">
                  <Input
                    type="number" min={0} value={draft.maxDays}
                    onChange={(e) => setDraft({ ...draft, maxDays: Number(e.target.value) })}
                  />
                </FormField>
                <FormField label="1년차 가산(일)">
                  <Input
                    type="number" min={0} value={draft.firstYearBonus}
                    onChange={(e) => setDraft({ ...draft, firstYearBonus: Number(e.target.value) })}
                  />
                </FormField>
                <Checkbox
                  className="lvp__carry"
                  label="남은 연차를 다음 해로 이월합니다"
                  checked={draft.carryOver}
                  onChange={(e) => setDraft({ ...draft, carryOver: e.target.checked })}
                />
              </div>

              <h3 className="lvp__sub">구간</h3>
              <div className="lvp__tierlist">
                {draft.tiers.map((t, i) => {
                  const kind = tierKindOf(t);
                  return (
                    <div key={i} className="lvp__tier">
                      {/*
                        ★★ 이름은 `aria-label` 이 만든다 — 한 폼에 「일수」가 여럿이라
                        보이는 라벨만으로는 **몇 번째 구간인지 갈리지 않는다**.
                      */}
                      <FormField label="종류">
                        <Select
                          ariaLabel={`${i + 1}번째 구간 종류`}
                          value={kind}
                          onChange={(v) => setKind(i, v as TierKind)}
                          options={KIND_OPTIONS}
                        />
                      </FormField>
                      <FormField label="근속(개월)">
                        <Input
                          wrapperClassName="lvp__numbox"
                          aria-label={`${i + 1}번째 구간 근속(개월)`}
                          type="number" min={0} value={t.months}
                          onChange={(e) => patchTier(i, { months: Number(e.target.value) })}
                        />
                      </FormField>
                      {kind === 'MONTHLY' && (
                        <>
                          <FormField label="달마다(일)">
                            <Input wrapperClassName="lvp__numbox" aria-label={`${i + 1}번째 구간 달마다(일)`}
                              type="number" min={0} value={t.perMonth ?? 0}
                              onChange={(e) => patchTier(i, { perMonth: Number(e.target.value) })} />
                          </FormField>
                          <FormField label="최대(일)">
                            <Input wrapperClassName="lvp__numbox" aria-label={`${i + 1}번째 구간 최대(일)`}
                              type="number" min={0} value={t.max ?? 0}
                              onChange={(e) => patchTier(i, { max: Number(e.target.value) })} />
                          </FormField>
                        </>
                      )}
                      {kind === 'FIXED' && (
                        <FormField label="일수">
                          <Input wrapperClassName="lvp__numbox" aria-label={`${i + 1}번째 구간 일수`}
                            type="number" min={0} value={t.days ?? 0}
                            onChange={(e) => patchTier(i, { days: Number(e.target.value) })} />
                        </FormField>
                      )}
                      {kind === 'STEP' && (
                        <>
                          <FormField label="시작 일수">
                            <Input wrapperClassName="lvp__numbox" aria-label={`${i + 1}번째 구간 시작 일수`}
                              type="number" min={0} value={t.days ?? 0}
                              onChange={(e) => patchTier(i, { days: Number(e.target.value) })} />
                          </FormField>
                          <FormField label="몇 개월마다">
                            <Input wrapperClassName="lvp__numbox" aria-label={`${i + 1}번째 구간 몇 개월마다`}
                              type="number" min={0} value={t.everyMonths ?? 0}
                              onChange={(e) => patchTier(i, { everyMonths: Number(e.target.value) })} />
                          </FormField>
                          <FormField label="+일수">
                            <Input wrapperClassName="lvp__numbox" aria-label={`${i + 1}번째 구간 +일수`}
                              type="number" min={0} value={t.plus ?? 0}
                              onChange={(e) => patchTier(i, { plus: Number(e.target.value) })} />
                          </FormField>
                        </>
                      )}
                      <Button
                        variant="quiet-danger"
                        size="sm"
                        className="lvp__del"
                        aria-label={`${i + 1}번째 구간 빼기`}
                        onClick={() => setDraft({ ...draft, tiers: draft.tiers.filter((_, k) => k !== i) })}
                      >
                        빼기
                      </Button>
                    </div>
                  );
                })}
                <Button
                  variant="secondary"
                  className="lvp__addtier"
                  iconLeft={<Plus size={14} aria-hidden />}
                  onClick={() => setDraft({ ...draft, tiers: [...draft.tiers, emptyTier('FIXED')] })}
                >
                  구간 더하기
                </Button>
              </div>

              {/* ★ 사람 말로 한 번 더 — 숫자만 보고는 무엇을 정했는지 알기 어렵다. */}
              <h3 className="lvp__sub">이렇게 됩니다</h3>
              <ul className="lvp__preview">
                {draft.tiers.length === 0
                  ? <li className="lvp__warn">구간이 없습니다.</li>
                  : draft.tiers.map((t, i) => <li key={i}>{tierText(t, draft.maxDays)}</li>)}
              </ul>

              {backdated && (
                <p className="lvp__warn" role="status">
                  <AlertCircle size={14} aria-hidden />
                  시행일자가 <b>오늘보다 앞</b>입니다 — 그 사이에 이미 계산된 연차가 달라질 수 있습니다.
                </p>
              )}
              {warnings.map((w) => (
                <p key={w.code} className="lvp__warn" role="status">
                  <AlertCircle size={14} aria-hidden />
                  {w.text.replace(/\*\*/g, '')}
                </p>
              ))}

              <div className="lvp__actions">
                <Button variant="secondary" onClick={() => setDraft(null)}>그만두기</Button>
                <Button
                  iconLeft={<Save size={16} aria-hidden />}
                  onClick={() => { void submit(); }}
                  loading={busy}
                  disabled={draft.tiers.length === 0}
                >
                  이 판으로 만들기
                </Button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default LeavePolicyPage;
